import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import ButtonPrimary from '../../buttons/primary/primary';
import SecondaryButton from '../../buttons/secondary/secondary';
import Loader from '../../loader/loader';
import { useAlert } from '../../alert/alert';
import { SEVERITY } from '../../../shared/interfaces/alert';
import { fetchRubricasGrouped } from '../../../features/rubrica/service/rubrica';
import { ProjectRubrica } from '../../../shared/interfaces/rubrica';
import './style.css';
import RubricaField from './rubrica-field';
import storage from '../../../shared/services/storage';
import { getMyAccessService } from '../../../features/user/service/user-service';

interface Rubrica {
  id: string;
  name: string;
  description: string;
  typeId?: string;
  value: string;
  label: string;
  group?: string;
}

interface RubricaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (rubricas: ProjectRubrica[]) => void;
  initialRubricas?: ProjectRubrica[];
  isLoading?: boolean;
}

interface FormData {
  rubricas: ProjectRubrica[];
}

export default function RubricaModal({ 
  isOpen, 
  onClose, 
  onSave, 
  initialRubricas = [],
  isLoading = false 
}: RubricaModalProps) {
  const { showAlert } = useAlert();
  const [availableRubricas, setAvailableRubricas] = useState<Rubrica[]>([]);
  const [isLoadingRubricas, setIsLoadingRubricas] = useState(false);
  const [userCompanyId, setUserCompanyId] = useState<string | null>(null);
  const [canCreateRubrica, setCanCreateRubrica] = useState(false);

  const formMethods = useForm<FormData>({
    defaultValues: {
      rubricas: initialRubricas.length > 0 ? initialRubricas : [{ rubricaId: '', valor: 0 }]
    },
    mode: 'onChange'
  });

  const { control, handleSubmit, reset, watch, formState: { errors, isValid } } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rubricas'
  });

  const watchedRubricas = watch('rubricas');

  const fetchRubricasData = useCallback(async (clientId?: string) => {
    setIsLoadingRubricas(true);
    try {
      const data = await fetchRubricasGrouped();
      
      if (Array.isArray(data)) {
        const formattedRubricas = data.map((rubrica: any) => ({
          id: rubrica.value,
          name: rubrica.label,
          description: rubrica.label,
          value: rubrica.value,
          label: rubrica.label,
          group: rubrica.group || 'Sem grupo'
        }));
        
        setAvailableRubricas(formattedRubricas);
      } else {
        throw new Error('Dados da API não estão no formato esperado');
      }
    } catch (error) {
      showAlert('Erro ao carregar rubricas', SEVERITY.ERROR);
      setAvailableRubricas([]);
    } finally {
      setIsLoadingRubricas(false);
    }
  }, [showAlert]);

  const checkCanCreateRubrica = (myAccess: any, userRole?: string): boolean => {
    if (userRole?.toLowerCase() === 'admin' || 
        myAccess?.role?.name?.toLowerCase() === 'admin' ||
        myAccess?.roleName?.toLowerCase() === 'admin') {
      return true;
    }

    let verboseAccessString = '';
    
    if (myAccess?.role?.verboseAccess) {
      verboseAccessString = myAccess.role.verboseAccess;
    } else if (myAccess?.verboseAccess) {
      verboseAccessString = myAccess.verboseAccess;
    } else if (myAccess?.permissions) {
      verboseAccessString = myAccess.permissions;
    }

    if (!verboseAccessString || verboseAccessString.trim() === '') {
      return true;
    }

    const hasPostPermission = verboseAccessString.toUpperCase().includes('POST');
    const hasPutPermission = verboseAccessString.toUpperCase().includes('PUT');
    const hasCreatePermission = verboseAccessString.toLowerCase().includes('create');
    const hasWritePermission = verboseAccessString.toLowerCase().includes('write');

    const canCreate = hasPostPermission || hasPutPermission || hasCreatePermission || hasWritePermission;
    
    return canCreate;
  };

  const fetchUserData = useCallback(async () => {
    try {
      const userLogged = await storage.getStorage('user');
      
      if (!userLogged) {
        await fetchRubricasData();
        return;
      }

      const user = JSON.parse(userLogged);
      
      const companyId = user?.company?.id;
      const userRole = user?.role?.name || user?.roleName || user?.role;
      
      if (companyId) {
        setUserCompanyId(companyId);
      }

      try {
        const myAccess: any = await getMyAccessService(user.id);
        const canCreate = checkCanCreateRubrica(myAccess, userRole);
        setCanCreateRubrica(canCreate);
      } catch (error) {
        if (companyId) {
          setCanCreateRubrica(true);
        } else {
          setCanCreateRubrica(false);
        }
      }

      if (companyId) {
        await fetchRubricasData(companyId);
      } else {
        await fetchRubricasData();
      }
    } catch (error) {
      await fetchRubricasData();
    }
  }, [fetchRubricasData]);

  const handleRubricaCreated = useCallback((newRubrica: Rubrica) => {
    
    setAvailableRubricas(prev => {
      const exists = prev.some(r => r.id === newRubrica.id);
      if (exists) {
        return prev;
      }
      return [...prev, newRubrica];
    });
    
    showAlert('Rubrica criada com sucesso!', SEVERITY.SUCCESS);
    
    setTimeout(() => {
      if (userCompanyId) {
        fetchRubricasData(userCompanyId);
      }
    }, 300);
  }, [showAlert, userCompanyId, fetchRubricasData]);

  const addRubrica = () => {
    append({ rubricaId: '', valor: 0 });
  };

  const removeRubrica = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const getTotalValue = () => {
    return watchedRubricas.reduce((total, rubrica) => total + Number(rubrica.valor || 0), 0);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getUsedRubricaIds = useCallback(() => {
    return watchedRubricas.map(r => r.rubricaId).filter(id => id);
  }, [watchedRubricas]);

  const onSubmit = (data: FormData) => {
    const validRubricas = data.rubricas
      .filter(r => r.rubricaId && r.valor > 0)
      .map(rubrica => {
        const fullRubrica = availableRubricas.find(r => r.id === rubrica.rubricaId);
        
        const formatted = {
          rubricaId: rubrica.rubricaId,
          valor: Number(rubrica.valor),
          name: fullRubrica?.name || fullRubrica?.label || '',
          description: fullRubrica?.description || fullRubrica?.label || ''
        };
        
        return formatted;
      });
    
    if (validRubricas.length === 0) {
      showAlert('Adicione pelo menos uma rubrica com valor maior que zero', SEVERITY.WARNING);
      return;
    }

    onSave(validRubricas);
    
    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      fetchUserData();
      reset({
        rubricas: initialRubricas.length > 0 ? initialRubricas : [{ rubricaId: '', valor: 0 }]
      });
    }
  }, [isOpen, initialRubricas, reset, fetchUserData]);

  if (!isOpen) return null;

  const hasAvailableRubricas = availableRubricas.length > getUsedRubricaIds().length;

  return (
    <FormProvider {...formMethods}>
      <div className="rubrica-modal-overlay">
        <div className="rubrica-modal">
          {(isLoading || isLoadingRubricas) && (
            <div className="rubrica-modal-loader">
              <Loader />
            </div>
          )}
          
          <div className="rubrica-modal__header">
            <h2 className="rubrica-modal__title">Gerenciar Rubricas do Projeto</h2>
            <button 
              className="rubrica-modal__close" 
              onClick={handleCancel}
              type="button"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="rubrica-modal__form">
            <div className="rubrica-modal__content">
              <div className="rubrica-modal__info">
                <p className="rubrica-modal__description">
                  Selecione as rubricas que fazem parte deste projeto e defina o valor para cada uma.
                </p>
              </div>

              <div className="rubrica-modal__fields">
                {fields.map((field, index) => (
                  <RubricaField
                    key={field.id}
                    fieldId={field.id}
                    index={index}
                    availableRubricas={availableRubricas}
                    onRemove={() => removeRubrica(index)}
                    isLoading={isLoading}
                    isLoadingRubricas={isLoadingRubricas}
                    canRemove={fields.length > 1}
                    getUsedRubricaIds={getUsedRubricaIds}
                    canCreateRubrica={canCreateRubrica}
                    userCompanyId={userCompanyId || undefined}
                    onRubricaCreated={handleRubricaCreated}
                  />
                ))}
              </div>

              <div className="rubrica-modal__add">
                <button
                  type="button"
                  onClick={addRubrica}
                  className="rubrica-modal__add-button"
                  disabled={isLoading || isLoadingRubricas || !hasAvailableRubricas}
                >
                  + Adicionar Rubrica
                </button>
              </div>

              {watchedRubricas.length > 0 && getTotalValue() > 0 && (
                <div className="rubrica-modal__summary">
                  <div className="rubrica-summary">
                    <div className="rubrica-summary__row">
                      <span className="rubrica-summary__label">Total de Rubricas:</span>
                      <span className="rubrica-summary__value">
                        {watchedRubricas.filter(r => r.rubricaId && r.valor > 0).length}
                      </span>
                    </div>
                    <div className="rubrica-summary__row rubrica-summary__total">
                      <span className="rubrica-summary__label">Valor Total do Projeto:</span>
                      <span className="rubrica-summary__value">
                        {formatCurrency(getTotalValue())}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="rubrica-modal__actions">
              <SecondaryButton
                title="Cancelar"
                onClick={handleCancel}
                disabled={isLoading}
              />
              <ButtonPrimary
                title="Salvar Rubricas"
                disabled={!isValid || isLoading || isLoadingRubricas}
              />
            </div>
          </form>
        </div>
      </div>
    </FormProvider>
  );
}
